/**
 * Component Styles Utility
 *
 * Handles Vue component style extraction and injection for .ce.vue files.
 * Vue 3 embeds styles in component definitions instead of auto-injecting them.
 *
 * This file provides:
 * - Style extraction for manual use (e.g., Shadow DOM)
 * - Style injection mixin for Vue apps
 * - Core utilities used by defineCustomElementWithStyles
 */

import type { App, Component } from 'vue';

// Import core chatbot components
import Chat from '@chatbot/components/Chat/Chat.ce.vue';
import ChatHeader from '@chatbot/components/Chat/ChatHeader.ce.vue';
import ChatInput from '@chatbot/components/Chat/ChatInput.ce.vue';
import ChatMessage from '@chatbot/components/Chat/ChatMessage.ce.vue';
import ChatThinkingIndicator from '@chatbot/components/Chat/ChatThinkingIndicator.ce.vue';
import ToggleChatButton from '@chatbot/components/Chat/ToggleChatButton.ce.vue';

// ============================================================================
// CORE UTILITIES (used internally and by defineCustomElementWithStyles)
// ============================================================================

interface ComponentWithStyles {
    __styles?: string[];
    styles?: string[];
}

/**
 * Finds the nearest element node in the DOM tree
 */
export const nearestElement = (node: HTMLElement | null): HTMLElement | null => {
    let current = node;
    while (current?.nodeType !== 1 /* ELEMENT */) {
        current = current?.parentElement ?? null;
    }
    return current;
};

/**
 * Extracts styles from a single component
 */
export const getComponentStyles = (component: Component): string[] => {
    const comp = component as ComponentWithStyles;
    const styles = comp.__styles ?? comp.styles ?? [];
    return Array.isArray(styles) ? styles : [styles];
};

/**
 * Injects styles into the DOM
 */
export const injectStyles = (styles: string[] | undefined, targetElement: HTMLElement): HTMLStyleElement | null => {
    if (!styles?.length) return null;

    const styleEl = document.createElement('style');
    styleEl.innerText = styles
        .join('')
        .replace(/\n/gu, '');
    nearestElement(targetElement)
        ?.prepend(styleEl);

    return styleEl;
};

// ============================================================================
// STYLE EXTRACTION (for manual use, e.g., Shadow DOM)
// ============================================================================

/**
 * Core chat components (most commonly needed for integration)
 */
export const coreChatComponents: Component[] = [
    Chat,
    ChatHeader,
    ChatInput,
    ChatMessage,
    ChatThinkingIndicator,
    ToggleChatButton,
];

/**
 * Extracts compiled CSS styles from Vue components
 *
 * @param components - Array of Vue components to extract styles from
 * @returns Combined CSS string of all component styles
 *
 * @example
 * ```typescript
 * import { extractComponentStyles, coreChatComponents } from '@chatbot/utils/component-styles';
 *
 * const styles = extractComponentStyles(coreChatComponents);
 * // Use styles string to inject into shadow DOM
 * ```
 */
export const extractComponentStyles = (components: Component[]): string => components
    .flatMap(getComponentStyles)
    .filter(Boolean)
    .join('\n\n');

/**
 * Extracts styles from core chat components only
 * Convenience function for the most common use case
 *
 * @returns Combined CSS string of core chat component styles
 */
export const extractChatStyles = (): string => extractComponentStyles(coreChatComponents);

// ============================================================================
// STYLE INJECTION MIXIN (for Vue apps)
// ============================================================================

interface ComponentInstanceWithStyles {
    $?: {
        type: {
            styles?: string[];
        };
    };
    $el: HTMLElement;
    $options: {
        components?: Record<string, { styles?: string[] }>;
    };
    __style?: HTMLStyleElement | null;
    __childStyles?: HTMLStyleElement[];
}

/**
 * Adds style injection mixin to a Vue app instance
 *
 * When using .ce.vue files, Vue embeds styles in the component definition
 * instead of injecting them into the DOM. This mixin automatically injects
 * those styles when components mount.
 *
 * @param app - Vue app instance to add the mixin to
 *
 * @example
 * ```typescript
 * import { createApp } from 'vue';
 * import { addStyleInjectionMixin } from '@chatbot/utils/component-styles';
 * import App from './App.ce.vue';
 *
 * const app = createApp(App);
 * addStyleInjectionMixin(app);
 * app.mount('#app');
 * ```
 */
export const addStyleInjectionMixin = (app: App): void => {
    app.mixin({
        // eslint-disable-next-line vue/component-api-style
        mounted(this: ComponentInstanceWithStyles) {
            // Inject component's own styles
            const componentStyles = this.$?.type?.styles;
            // eslint-disable-next-line vue/no-undef-properties
            this.__style = injectStyles(componentStyles, this.$el);

            // Inject styles from child components
            if (this.$options.components) {
                // eslint-disable-next-line vue/no-undef-properties
                this.__childStyles = [];
                const { components } = this.$options;
                for (const comp of Object.values(components)) {
                    const childStyles = comp?.styles;
                    const styleElement = injectStyles(childStyles, this.$el);
                    if (styleElement) {
                        this.__childStyles.push(styleElement);
                    }
                }
            }
        },
        // eslint-disable-next-line vue/component-api-style
        unmounted(this: ComponentInstanceWithStyles) {
            // Clean up component's own styles
            this.__style?.remove();

            // Clean up child component styles
            this.__childStyles?.forEach(style => {
                style.remove();
            });
        },
    });
};
